import { useState } from "react";
import { offerComparison, type ReviewOffer } from "./offerComparison";
import { offerSelectionScope, purchaseSelection, rankOfferRow, suggestPurchaseChoices, type PurchaseChoices } from "./offerSelection";

type SelectionState = { scope: string; stage: "compare" | "choose" | "summary"; choices: PurchaseChoices };
export function useOfferSelection(offers: ReviewOffer[], initialChoices?: PurchaseChoices) {
  const scope = offerSelectionScope(offers);
  const empty: SelectionState = { scope, stage: "compare", choices: {} };
  const [stored, setStored] = useState<SelectionState>(() => initialChoices ? { scope, stage: "summary", choices: initialChoices } : empty);
  // Permanently discard a previous source scope. Deriving an empty view alone
  // would resurrect the old choices on A → B → A without a remount.
  if (stored.scope !== scope) setStored(empty);
  const current = stored.scope === scope ? stored : empty;
  function update(change: (state: SelectionState) => SelectionState) {
    setStored(state => change(state.scope === scope ? state : empty));
  }
  return {
    saved: Boolean(initialChoices),
    stage: current.stage, choices: current.choices, selection: purchaseSelection(offers, current.choices),
    start: () => update(state => ({ ...state, stage: "choose" })),
    summarize: () => update(state => ({ ...state, stage: "summary" })),
    suggest: () => update(state => ({ ...state, choices: suggestPurchaseChoices(offers, state.choices) })),
    clear: () => update(state => ({ ...state, choices: {} })),
    end: () => setStored(initialChoices ? { scope, stage: "summary", choices: initialChoices } : empty),
    choose: (key: string, offerId: string | null) => {
      const row = offerComparison(offers).find(row => row.key === key);
      if (!row || !rankOfferRow(row) || (offerId !== null && !offers.some(offer => offer.id === offerId))) return;
      update(state => ({ ...state, choices: { ...state.choices, [key]: { offerId, method: "manual" } } }));
    },
  };
}
